/*
 * WebViewErrorBoundary.tsx - Error boundary for WebView bridge issues
 *
 * This component catches JavaScript errors that occur in WebView environments,
 * particularly "Java object is gone" errors that happen when the WebView bridge
 * is destroyed or becomes unavailable.
 *
 * Features:
 * - Catches and logs WebView-specific errors
 * - Provides graceful fallback UI
 * - Prevents app crashes from bridge disconnections
 */

import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
}

class WebViewErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a WebView bridge error
    const isWebViewError = 
      error.message?.includes('Java object is gone') ||
      error.message?.includes('bridge') ||
      error.message?.includes('WebView') ||
      error.stack?.includes('sentryWrapped');

    if (isWebViewError) {
      console.warn('WebView bridge error caught:', error);
      return { 
        hasError: true, 
        errorMessage: 'WebView connection issue detected'
      };
    }

    // For non-WebView errors, let them bubble up
    throw error;
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.warn('WebView Error Boundary caught an error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString()
    });

    // Attempt to recover by clearing potentially corrupted state
    setTimeout(() => {
      if (this.state.hasError) {
        this.setState({ hasError: false, errorMessage: '' });
      }
    }, 3000);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-[#EFEDE4]">
          <div className="text-center max-w-md mx-auto">
            <div className="mb-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#F2B8B8] flex items-center justify-center">
                <span className="text-2xl">⚠️</span>
              </div>
            </div>
            <h2 className="text-lg font-semibold text-[#3B3C36] mb-2">
              Connection Issue
            </h2>
            <p className="text-sm text-[#93938D] mb-4">
              We're experiencing a temporary connection issue. The app will automatically recover.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-[#105142] text-white rounded-lg hover:bg-[#0d4235] transition-colors"
            >
              Refresh App
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default WebViewErrorBoundary;
